import * as React from 'react';

import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import AIMessageModal from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/AIMessageModal';
import type { AIMessageModalApi } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/AIMessageModalContext';
import { AIMessageModalContext } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/AIMessageModalContext';
import type {
  AudienceSource,
  CtaOverride,
  OpenAIMessageModalInput,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { MessagingTrackingDataProvider } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import { AI_MESSAGE_ACTIONS } from 'bundles/enterprise-admin-messaging-v2/utils/aiMessageActions';

type Props = {
  children: React.ReactNode;
  /**
   * Called when the modal requests to close (e.g. the user dismisses it).
   * When provided it replaces the default internal close, letting the route
   * own the open/closed state — the page navigates away from `/new` and the
   * resulting route change closes the modal.
   */
  onClose?: () => void;
};

const resolveInitialPrompt = (input: OpenAIMessageModalInput | undefined): string => {
  if (!input) return '';
  if (input.prompt !== undefined) return input.prompt;
  if (input.actionKey) return AI_MESSAGE_ACTIONS[input.actionKey].prompt;
  return '';
};

/**
 * Owns the "is the AI Message modal open + with what initial prompt" state
 * and renders the modal in-place (CDS Dialog handles the portal). Mount
 * once near the admin-dashboard root so any descendant can call
 * `useAIMessageModal().openModal({ prompt | actionKey })` from anywhere.
 */
const AIMessageModalProvider = ({ children, onClose }: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [initialPrompt, setInitialPrompt] = React.useState('');
  const [autoGenerate, setAutoGenerate] = React.useState(false);
  const [audienceSource, setAudienceSource] = React.useState<AudienceSource | undefined>(undefined);
  const [showAutomateScreen, setShowAutomateScreen] = React.useState(true);
  const [ctaOverride, setCtaOverride] = React.useState<CtaOverride | undefined>(undefined);
  // Stored as `() => callback` so React doesn't treat the callback as a state updater.
  const [onSent, setOnSent] = React.useState<(() => void) | undefined>(undefined);
  const trackingData = useEnterpriseAdminTrackingData();

  const api = React.useMemo<AIMessageModalApi>(
    () => ({
      openModal: (input) => {
        setInitialPrompt(resolveInitialPrompt(input));
        setAutoGenerate(input?.autoGenerate ?? false);
        setAudienceSource(input?.audienceSource);
        setShowAutomateScreen(input?.showAutomateScreen ?? true);
        setCtaOverride(input?.ctaOverride);
        setOnSent(() => input?.onSent);
        setIsOpen(true);
      },
      closeModal: () => setIsOpen(false),
    }),
    []
  );

  // Always close the modal internally, then run the optional external onClose
  // (route cleanup). Closing internally is what makes an imperative open —
  // openModal() from a recommended-actions card, with no route change —
  // dismissible; the navigation-only onClose can't, since it's a no-op when the
  // route is already the base path.
  const handleClose = React.useCallback(() => {
    api.closeModal();
    onClose?.();
  }, [api, onClose]);

  return (
    <AIMessageModalContext.Provider value={api}>
      {children}
      {/* Supplies the shared enterprise + admin-account entities to the modal's
          tracking, since it's mounted near the dashboard root (opened from many
          pages), not inside the messaging page's provider. */}
      <MessagingTrackingDataProvider value={trackingData}>
        <AIMessageModal
          open={isOpen}
          initialPrompt={initialPrompt}
          autoGenerate={autoGenerate}
          audienceSource={audienceSource}
          showAutomateScreen={showAutomateScreen}
          ctaOverride={ctaOverride}
          onClose={handleClose}
          onSent={onSent}
        />
      </MessagingTrackingDataProvider>
    </AIMessageModalContext.Provider>
  );
};

export default AIMessageModalProvider;
